import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ChevronRight, MessageCircle, Search } from "lucide-react";
import { listMyConversations } from "@/lib/messages.functions";
import { StatusPill } from "@/components/StatusPill";
import { AppHeader } from "@/components/site/AppHeader";
import { Input } from "@/components/ui/input";

const conversationsQuery = queryOptions({
  queryKey: ["conversations"],
  queryFn: () => listMyConversations(),
});

export const Route = createFileRoute("/_authenticated/messages/")({
  head: () => ({
    meta: [
      { title: "Messages — Shields" },
      { name: "description", content: "Your Shields intranet — message the people on your jobs." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MessagesInbox,
});

export function MessagesInbox({ workspace = "customer" }: { workspace?: "customer" | "partner" }) {
  const { data: conversations } = useSuspenseQuery(conversationsQuery);
  const [search, setSearch] = useState("");
  const isPartner = workspace === "partner";
  const visible = conversations.filter((conversation) =>
    `${conversation.otherName} ${conversation.serviceName} ${conversation.reference}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-background pb-28">
      <AppHeader workspace={workspace} roleLabel={isPartner ? "Partner" : "Customer"} />

      <main className="product-page layout-inbox section-pad mx-auto py-7 sm:py-10">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-muted-foreground">
          {isPartner ? "Partner job communication" : "Customer support and job communication"}
        </p>
        <h1 className="mt-1 flex items-center gap-3 text-2xl font-extrabold sm:text-3xl">
          <MessageCircle className="h-7 w-7 text-primary" aria-hidden /> Chats
        </h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          {isPartner
            ? "Speak with Customers only about work assigned to your Partner account."
            : "Keep conversations about your bookings together. Contact details stay protected until assignment."}
        </p>

        {conversations.length > 0 && (
          <label className="relative mt-5 block">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search chats"
              className="h-12 rounded-2xl bg-card pl-11"
            />
          </label>
        )}

        {visible.length === 0 ? (
          <div className="card-surface mt-10 p-10 text-center">
            <MessageCircle className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
            <h2 className="mt-4 font-display text-lg font-bold">
              {search ? "No chats match your search" : "No conversations yet"}
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {isPartner
                ? "A conversation appears when a job is assigned to your Partner account."
                : "Request a job and once a Partner is assigned you can message each other here."}
            </p>
            {isPartner ? (
              <Link to="/partner" className="btn-hero mt-6">
                Open Partner workspace
              </Link>
            ) : (
              <Link to="/book" className="btn-hero mt-6">
                Request a job
              </Link>
            )}
          </div>
        ) : (
          <ul className="mt-8 space-y-3">
            {visible.map((c) => (
              <li key={c.bookingId}>
                <Link
                  to={isPartner ? "/partner/messages/$bookingId" : "/messages/$bookingId"}
                  params={{ bookingId: c.bookingId }}
                  className="card-surface group flex items-center gap-4 p-4 transition hover:border-primary/25 hover:shadow-md sm:p-5"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-soft font-display font-extrabold text-primary">
                    {c.otherName.charAt(0)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold group-hover:text-primary">{c.otherName}</p>
                      <StatusPill status={c.status} />
                    </div>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {c.serviceName} · {c.reference}
                      {c.lastMessage ? ` — ${c.lastMessage}` : ""}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-primary"
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
